import {api} from "@/shared/api";

import {BasicDataTable, TypedColumnSettings} from "@/shared/ui/basicDataTable"
import {RecordingTable} from "@/shared/recording/recordingTable";
import {showWorkingTimeBulkResult} from "@/shared/bulkOperationHelper";
import {WorkingTimeBulkSelector} from "./workingTimeBulkSelector";
import {isRecordingModeUserAllowed, isWorkingTimeDateTimeChangeAllowed, isWorkingTimeDeletable, isWorkingTimeTypeRecordable} from "@/shared/securityHelper";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {
	formatDate,
	formatDateDay,
	formatDateTime,
	formatDuration,
	parseISODate,
	parseISOZonedDateTime
} from "@/shared/utils/datefunctions";
import {ReportWorkingTime, WorkingTimeBulkOperationResult, WorkingTimeDateSpan} from "@/shared/timr";
import {
	DurationUnit,
	RecordedEntryStatus,
	WorkingTimeTypeCategory,
	WorkingTimeTypeRecordingMode
} from "@/shared/timrTypes";

export class WorkingTimeTable extends RecordingTable<ReportWorkingTime, WorkingTimeBulkSelector> {
	public static readonly RECORDING_WORKING_TIMES_URL = "/timr/recording/getWorkingTimes.json";
	public static readonly REPORT_WORKING_TIMES_URL = "/timr/reports/getWorkingTimes.json";
	public static readonly EVENT_EDIT_DATE_SPAN = "editDateSpan";

	protected edit(recording: ReportWorkingTime) {
		if (recording.workingTimeDateSpanId !== null) {
			$(this).triggerHandler(WorkingTimeTable.EVENT_EDIT_DATE_SPAN, [recording]);
		} else {
			super.edit(recording);
		}
	}

	/**
	 * If new columns are added, also modify {@link #buildSubTableColumns} to prevent visual errors in the subtable.
	 */
	protected getColumnSettings(): TypedColumnSettings<ReportWorkingTime>[] {
		return [
			{
				data: "id",
				orderable: false,
				createdCell: this.renderRowBorder,
				render: BasicDataTable.renderSelectCheckbox,
				visible: this.config.allowSelect,
				width: BasicDataTable.COLUMN_SIZE_CHECKBOX
			},
			{
				data: "id",
				orderable: false,
				createdCell: (this.config.allowSelect ? null : this.renderRowBorder),
				className: "context-menu",
				render: this.contextMenu.render,
				width: BasicDataTable.COLUMN_SIZE_MENU
			},
			{
				data: "userName",
				orderable: this.config.orderable,
				render: BasicDataTable.renderText,
				visible: this.config.showUser
			},
			{
				data: "workingTimeTypeName",
				orderable: this.config.orderable,
				render: BasicDataTable.renderText,
				width: BasicDataTable.COLUMN_SIZE_TYPE_NAME
			},
			{
				data: "startPosition",
				orderable: false,
				render: BasicDataTable.renderPositionIcon,
				className: "gps-position gps-position-start no-padding",
				width: BasicDataTable.COLUMN_SIZE_GPS_ICON
			},
			{
				data: "start",
				orderable: this.config.orderable,
				render: BasicDataTable.renderDateTimeWithTimeZone,
				className: "pl-10 pr-10 time-column"
			},
			{
				data: "durationGross",
				orderable: false,
				render: RecordingTable.renderDuration,
				className: "text-right",
				width: BasicDataTable.COLUMN_SIZE_DURATION
			},
			{
				data: "breakTime",
				orderable: false,
				render: RecordingTable.renderBreakTime,
				className: "text-center break-time-popover-anchor",
				width: BasicDataTable.COLUMN_SIZE_BREAKTIME
			},
			{
				data: "durationNet",
				orderable: false,
				render: BasicDataTable.renderDuration,
				className: "text-right",
				width: BasicDataTable.COLUMN_SIZE_DURATION_NET
			},
			{
				data: "description",
				orderable: this.config.orderable,
				className: "table-data-description",
				render: BasicDataTable.renderDescriptionText
			},
			{
				data: "status",
				orderable: false,
				render: this.renderQuickSwitch,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_QUICK_SWITCH_BUTTON,
				visible: this.config.allowQuickSwitch
			},
			{
				data: "validations",
				orderable: false,
				render: (d, t, r, m) => this.renderValidationWarning(d, t, r, m),
				className: "slim-icon-column validations",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			},
			{
				data: "status",
				orderable: false,
				render: RecordingTable.renderStatus,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[5, "desc"]];
	}

	protected isStatusNumberEnabled(statusNumber: RecordedEntryStatus, recording: ReportWorkingTime): boolean {
		return super.isStatusNumberEnabled(statusNumber, recording) && ((statusNumber === RecordedEntryStatus.Closed || statusNumber === RecordedEntryStatus.Archived) || !recording.hasTimeAccount);
	}

	protected delete(recording: ReportWorkingTime) {
		super.delete(recording);

		if (recording.workingTimeDateSpanId != null) {
			api("workingTimeDateSpans/findWorkingTimeDateSpanByWorkingTimeId.json")
				.get({workingTimeId: recording.id})
				.done((workingTimeDateSpan) => {
					BootstrapModal.confirm({
						title: "workingtime.delete.title",
						message: messages.withArguments("workingTimeDateSpans.dialog.delete.message",
							workingTimeDateSpan.workingTimeType.name,
							formatDate(parseISODate(workingTimeDateSpan.start)),
							formatDate(parseISODate(workingTimeDateSpan.end))
						),
						yesLabel: "confirmation.delete",
						noLabel: "confirmation.cancel",
						yesAction: (modal: BootstrapModal.Modal) => {
							api("workingTimeDateSpans/deleteWorkingTimeDateSpan.json")
								.post({
									workingTimeDateSpanId: workingTimeDateSpan.id,
								})
								.done(() => {
									this.reload();
								}).always(() => {
								modal.close();
							});
						}
					});
				});
		} else if (recording.status === RecordedEntryStatus.Changeable) {
			const start = parseISOZonedDateTime(recording.start);
			BootstrapModal.confirm({
				title: "workingtime.delete.title",
				message: messages.withArguments(
					"workingtime.delete.message",
					recording.workingTimeTypeName || "",
					recording.description ? " " + messages.withArguments("record.delete.message.description", recording.description) : "",
					recording.editUnit === DurationUnit.Minutes ? formatDateTime(start) : formatDate(start)
				),
				yesLabel: "confirmation.delete",
				noLabel: "confirmation.cancel",
				yesAction: (modal: BootstrapModal.Modal, button?: JQuery) => {
					api("recording/deleteWorkingTime.json")
						.post({
							id: recording.id
						}).done(() => {
						this.reload();
					}).always(() => {
						modal.close();
					});
				}
			});
		}
	}

	protected renderQuickSwitch(this: void, data: RecordedEntryStatus, type: string, recording: ReportWorkingTime, meta: unknown): string {
		return recording.editUnit === DurationUnit.Minutes &&
		recording.workingTimeTypeCategory !== WorkingTimeTypeCategory.BreakTime &&
		// hide quick start/switch for types the current user is not allowed to record (e.g. admin-only minute-based types)
		isWorkingTimeTypeRecordable(recording.workingTimeTypeRecordingModeUser) ?
			super.renderQuickSwitch(data, type, recording, meta, recording.workingTimeTypeName) : "";
	}

	protected renderValidationWarning(data: unknown, type: string, recording: ReportWorkingTime, meta: unknown): string | null {
		if (globals.savedValidationsEnabled) {
			return this.renderSeverityIcon(recording.validations?.relevant ?? []);
		} else {
			return null;
		}
	}

	protected renderPopoverContent(this: void, recording: ReportWorkingTime, rowClass?: string): string {
		return recording.workingTimeTypeCategory === WorkingTimeTypeCategory.AttendanceTime ? super.renderPopoverContent(recording, rowClass) : "";
	}

	protected showRevisions(recording: ReportWorkingTime, tr: JQuery) {
		super.showRevisions(recording, tr);

		if (!this.dataTable.row(tr).child.isShown()) {
			if (recording.workingTimeDateSpanId !== null) {
				api("workingTimeDateSpans/findWorkingTimeDateSpanRevisions.json")
					.get(this.extendAjaxData({workingTimeDateSpanId: recording.workingTimeDateSpanId}, this.getRoundingFilterParams()))
					.done(workingTimeDateSpanRevisions => {
						this.renderDateSpanSubTable(workingTimeDateSpanRevisions.data, tr, WorkingTimeTable.ROW_CLASS_REVISION, (datespan: WorkingTimeDateSpan) => this.buildDateSpanRevisionRow(datespan));
					});
			} else {
				api("reports/getWorkingTimeRevisions.json")
					.get(this.extendAjaxData({id: recording.id}, this.getRoundingFilterParams()))
					.done(workingTimeRevisions => {
						this.renderSubTable(workingTimeRevisions.data, tr, WorkingTimeTable.ROW_CLASS_REVISION, (revision) => this.buildRevisionRow(revision));
					});
			}
		} else {
			this.renderSubTable(null, tr, WorkingTimeTable.ROW_CLASS_REVISION, (revision) => this.buildRevisionRow(revision));
		}
	}

	protected buildDateSpanRevisionRow(recording: WorkingTimeDateSpan): JQuery {
		const date = parseISOZonedDateTime(recording.lastModified);

		return $("<tr></tr>").attr("role", "row").addClass(BasicDataTable.EXCLUDE_ROW_CLASS_DBLCLICK)
			.append($("<td></td>").attr("colspan", 100).addClass("td-modification-info")
				.append($("<div></div>")
					.append($("<span></span>").text(messages["report.column.lastModified"] + ": ").addClass("text-bold"))
					.append($("<span></span>").text(formatDateTime(date) + ", "))
					.append($("<span></span>").text(messages["report.column.modifiedByUser"] + ": ").addClass("text-bold"))
					.append($("<span></span>").text(recording.modifiedByUser.fullname))
					.addClass("div-modification-info")))
			.add(
				$("<tr></tr>").attr("role", "row").addClass(RecordingTable.ROW_CLASS_REVISION).addClass(BasicDataTable.EXCLUDE_ROW_CLASS_DBLCLICK)
					.append($("<td></td>").attr("colspan", this.config.allowSelect ? 2 : 1))
					.append(this.buildDateSpanSubTableColumns(recording)));
	}

	protected showConflicts(recording: ReportWorkingTime, tr: JQuery) {
		super.showConflicts(recording, tr);

		if (!this.dataTable.row(tr).child.isShown()) {
			api("reports/getConflictedWorkingTimes.json")
				.get(this.extendAjaxData({id: recording.id}, this.getRoundingFilterParams()))
				.done((data) => {
					this.renderSubTable(data.conflictedWorkingTimes, tr, WorkingTimeTable.ROW_CLASS_CONFLICT, (conflict) => this.buildConflictsRow(conflict));
				});
		} else {
			this.renderSubTable(null, tr, WorkingTimeTable.ROW_CLASS_CONFLICT, conflict => this.buildConflictsRow(conflict));
		}
	}

	protected markConflictAsResolved(recording: ReportWorkingTime) {
		super.markConflictAsResolved(recording);

		api("reports/markWorkingTimeConflictAsResolved.json")
			.post({
				id: recording.id
			})
			.done(() => {
				this.reload();
			});
	}

	protected setStatus(recording: ReportWorkingTime, statusNumber: number) {
		super.setStatus(recording, statusNumber);

		api("reports/updateWorkingTimeStatus.json")
			.post({
				statusNumber: statusNumber,
				idsInclude: recording.id,
				allSelected: false
			})
			.done((result: WorkingTimeBulkOperationResult) => {
				if (!result.allUpdated) {
					showWorkingTimeBulkResult(result, "report.setStatus.");
				}
				this.reload();
			});
	}

	protected renderRowBorder(this: void, td: unknown, type: unknown, recording: ReportWorkingTime, meta: unknown) {
		if (recording.workingTimeTypeCategory !== null) {
			$(td).addClass("border-row-" + recording.workingTimeTypeCategory);
		}
	}

	protected buildSubTableColumns(recording: ReportWorkingTime): JQuery[] {
		return [
			this.config.showUser ? this.buildTextColumn(recording.userName) : null,
			this.buildTextColumn(recording.workingTimeTypeName),
			this.buildPositionColumn(recording),
			this.buildDateTimeColumn(recording, recording.start),
			this.buildDurationColumn(recording, recording.durationGross),
			this.buildBreakTimeColumn(recording, recording.breakTime, "text-center"),
			this.buildDurationColumn(recording, recording.durationNet),
			this.buildHtmlColumn(BasicDataTable.renderDescriptionText(recording.description, null, recording, null), "table-data-description"),
			this.config.allowQuickSwitch ? this.buildEmptyColumn() : null,
			this.buildEmptyColumn(), /* we don't display the validations warning icon in revision or conflicted rows */
			this.buildIconColumn(RecordingTable.renderStatus(recording.status, null, recording, null))
		];
	}

	protected buildDateSpanSubTableColumns(dateSpan: WorkingTimeDateSpan): JQuery[] {
		return [
			this.config.showUser ? this.buildTextColumn(dateSpan.user.fullname) : null,
			this.buildTextColumn(dateSpan.workingTimeType.name),
			this.buildEmptyPositionColumn(),
			this.buildDateColumn(dateSpan, dateSpan.start),
			$("<td></td>").addClass("pl-10 pr-10"),
			$("<td></td>").addClass("pl-10 pr-10"),
			$("<td></td>").addClass("pl-10 pr-10"),
			this.buildHtmlColumn(BasicDataTable.renderDescriptionText(dateSpan.description, null, dateSpan, null), "table-data-description"),
			this.config.allowQuickSwitch ? this.buildEmptyColumn() : null,
			this.buildEmptyColumn(), /* we don't display the validations warning icon in revision or conflicted rows */
			this.buildEmptyColumn()
		];
	}

	protected buildDateColumn(recording: ReportWorkingTime | WorkingTimeDateSpan, dateTimeString: string): JQuery {
		return $("<td></td>").addClass("pl-10 pr-10")
			.html(this.renderDate(dateTimeString, null, recording, null));
	}

	protected renderDate(this: void, data: string, type: string, recording: {
		start: string;
		end?: string;
		changed?: boolean | string
	}, meta: unknown): string {
		const startDate = parseISOZonedDateTime(recording.start);
		const changed = recording.changed
			? `<div class="edited-pen"><i class="fal fa-pen" title="${messages["report.column.changed.title"]}"></i></div>` : "";
		if (recording.end) {
			const endDate = parseISOZonedDateTime(recording.end);

			if (formatDate(startDate) === formatDate(endDate)) {
				return `<div class="flexbox-container">
							<div>
								<div>
									${formatDateDay(startDate)}, <span class="text-size-small">${formatDate(startDate)}</span>
								</div>
							</div>
							${changed}
						</div>
					`;
			} else {
				return `<div class="flexbox-container">
							<div>
								<div>
									${formatDateDay(startDate)}, <span class="text-size-small">${formatDate(startDate)}</span>
								</div>
							</div>
							<div>
								<i class="time-separation-symbol"></i>
							</div>
							<div>
								<div>
									${formatDateDay(endDate)}, <span class="text-size-small">${formatDate(endDate)}</span>
								</div>
							</div>
							${changed}
						</div>`;
			}
		}
		return "";
	}

	protected createBulkSelector(): WorkingTimeBulkSelector {
		return new WorkingTimeBulkSelector({
			dataTable: this.dataTable
		});
	}

	protected recordingDuplicatable(recording: ReportWorkingTime): boolean {
		return recording.workingTimeTypeRecordingModeUser !== WorkingTimeTypeRecordingMode.BY_REQUEST &&
			(globals.isGroupLeader || isRecordingModeUserAllowed(recording.workingTimeTypeRecordingModeUser)) &&
			(recording.editUnit !== DurationUnit.Minutes || super.recordingDuplicatable(recording));
	}

	public recordingChangeable(recording: ReportWorkingTime): boolean {
		return super.recordingChangeable(recording) &&
			(globals.isGroupLeader || isRecordingModeUserAllowed(recording.workingTimeTypeRecordingModeUser)) &&
			!recording.hasTimeAccount && !recording.hasWorkingTimeRequest &&
			// DEVNOTE: WorkingTimeDateSpan is only changeable if all the other WorkingTimes are changeable as well
			(recording.workingTimeDateSpanId === null || recording.workingTimeDateSpanEditable);
	}

	protected recordingDeletable(recording: ReportWorkingTime): boolean {
		return isWorkingTimeDeletable(recording, this.isDateTimeChangeAllowed(false, recording.userId, this.config.allowedUserIds));
	}

	protected recordingResumable(recording: ReportWorkingTime): boolean {
		return super.recordingResumable(recording) && recording.editUnit === DurationUnit.Minutes &&
			(globals.isGroupLeader || isRecordingModeUserAllowed(recording.workingTimeTypeRecordingModeUser));
	}

	protected isDateTimeChangeAllowed(anyUser: boolean, userId: number, allowedUserIds: number []): boolean {
		return isWorkingTimeDateTimeChangeAllowed(anyUser, userId, allowedUserIds);
	}

	protected updateSelectedRowsCount(additionalSum?: string) {
		const selectedRowsCount = this.bulkSelector.getSelectedEntriesCount();
		const rowCountElement = $(".selected-rows-count");
		const rowSumElement = $(".selected-rows-sum");
		const sums = this.bulkSelector.getSums();
		const selectedRowsDurationNetSum = formatDuration(sums.durationNetSum);
		const selectedRowsDurationGrossSum = formatDuration(sums.durationGrossSum);
		const selectedRowsBreakTimeSum = formatDuration(sums.breakTimeSum);
		const additionalSumMessage = additionalSum ?? "";

		if (this.bulkSelector.isAllEntriesSelected() && !this.bulkSelector.isNoneSelected()) {
			rowCountElement.html(messages.withArguments("bulkselection.allselected", selectedRowsCount));
			rowSumElement.html(messages.withArguments("bulkselection.workingTime", selectedRowsDurationGrossSum, selectedRowsBreakTimeSum, selectedRowsDurationNetSum) + additionalSumMessage);
			this.bulkSelector.selectAllCheckbox();
		} else if (this.bulkSelector.isNoneSelected()) {
			rowCountElement.text("");
			rowSumElement.text("");
			this.bulkSelector.unSelectAllCheckbox();
		} else if (this.bulkSelector.getSelectedEntriesCount() === 1) {
			rowCountElement.html(messages["bulkselection.oneselected"]);
			rowSumElement.html(messages.withArguments("bulkselection.workingTime", selectedRowsDurationGrossSum, selectedRowsBreakTimeSum, selectedRowsDurationNetSum) + additionalSumMessage);
			this.bulkSelector.unSelectAllCheckbox();
		} else {
			rowCountElement.html(messages.withArguments("bulkselection.entriesselected", selectedRowsCount));
			rowSumElement.html(messages.withArguments("bulkselection.workingTime", selectedRowsDurationGrossSum, selectedRowsBreakTimeSum, selectedRowsDurationNetSum) + additionalSumMessage);
			this.bulkSelector.unSelectAllCheckbox();
		}
	}

	protected getRecordingStatus(recording: ReportWorkingTime): RecordedEntryStatus {
		return recording.status;
	}

	protected isConflicted(recording: ReportWorkingTime): boolean {
		return recording.conflicted;
	}
}
